// src/components/organization/projects/ProjectsSubHeader.tsx
import {
    Box,
    Button,
    Divider,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'src/components/DatePicker';
import { Filters } from 'src/components/Filters';
import SwitchButton from 'src/components/widgets/buttons/SwitchButton';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { ClearIcon } from 'src/icons/Clear';
import useHasPermissions from 'src/hooks/useHasPermission';
import { APPROVALS, CLIENT_CASES } from 'src/constants/constants';
import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';

interface ProjectsSubHeaderProps {
    projectName?: string;
    search: string;
    onSearch: (value: string) => void;
    filters: any;
    onRemoveFilter: (column: string) => void;
    hasAttachments: boolean;
    onSwitchChange: (checked: boolean) => void;
    startDate: Date | null;
    onStartDateChange: (date: Date | null) => void;
    endDate: Date | null;
    onEndDateChange: (date: Date | null) => void;
    onClearFilters: () => void;
    extraFilters?: ReactNode;
    breadcrumbData?: {
        title?: string;
        links?: Array<{
            name: string;
            to: string;
            color?: string;
        }>;
        color?: string;
    };
}

const ProjectsSubHeader = ({
    projectName,
    search,
    onSearch,
    filters,
    onRemoveFilter,
    hasAttachments,
    onSwitchChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    onClearFilters,
    extraFilters,
    breadcrumbData,
}: ProjectsSubHeaderProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const checkPermissions = useHasPermissions();
    const whiteLabel: any = JSON.parse(localStorage.getItem('white_label'));

    // Determine active tab based on current route
    const getCurrentTab = () => {
        const { pathname } = location;
        if (pathname.includes('/projects/') && pathname.includes('/all')) return 'all';
        if (pathname.includes('/projects/') && pathname.includes('/open')) return 'open';
        if (pathname.includes('/projects/') && pathname.includes('/approvals')) return 'approvals';
        if (pathname.includes('/projects/') && pathname.includes('/backlog')) return 'backlog';
        if (pathname.includes('/projects/') && pathname.includes('/testing')) return 'testing';
        return 'all'; // default
    };

    const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
        const projectId = location.pathname.split('/')[2]; // Extract project ID from URL
        navigate(`/projects/${projectId}/${newValue}`);
    };

    return (
        <Box>
            {/* Breadcrumb */}
            {breadcrumbData && (
                <Box sx={{ marginBottom: '12px' }}>
                    <SimpleBreadCrumb data={breadcrumbData} />
                </Box>
            )}

            {/* Title Row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px',
                    marginBottom: '16px',
                }}>
                {/* Title */}
                {projectName ? (
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#1A202C',
                            whiteSpace: 'nowrap',
                        }}>
                        {projectName}
                    </Typography>
                ) : (
                    <Skeleton
                        variant="text"
                        width={200}
                        height={32}
                    />
                )}
            </Box>

            {/* Search and Filters and Tabs */}
            <Box sx={{ marginBottom: '10px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        gap: '16px',
                    }}>
                    {/* Tabs */}
                    <Tabs
                        value={getCurrentTab()}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 'unset',
                            '& .MuiTabs-indicator': {
                                height: '2px',
                            },
                            '& .MuiTab-root': {
                                minHeight: 'unset',
                                minWidth: 'unset',
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#425466',
                                padding: '6px 12px',
                            },
                            '& .Mui-selected': {
                                color: '#1A202C',
                            },
                        }}>
                        {checkPermissions([CLIENT_CASES]) && <Tab label="All" value="all" />}
                        {checkPermissions([CLIENT_CASES]) && <Tab label="Open" value="open" />}
                        {(whiteLabel?.isApprovalRequired && checkPermissions([APPROVALS])) && (
                            <Tab label="Approvals" value="approvals" />
                        )}
                        {checkPermissions([CLIENT_CASES]) && <Tab label="Backlog" value="backlog" />}
                        <Tab label="Testing" value="testing" />
                    </Tabs>

                    <Stack
                        direction="row"
                        spacing={2}
                        divider={
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    height: '22px',
                                    alignSelf: 'center',
                                }}
                            />
                        }
                        alignItems="center"
                    >
                        <Box display="flex" alignItems="center" gap="10px">
                            <SearchBar
                                onSearch={onSearch}
                                value={search}
                                placeholderData="Search ..."
                            />
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: '#8492A6',
                                }}>
                                CASES WITH ATTACHMENTS
                            </Typography>
                            <SwitchButton
                                checked={hasAttachments}
                                onChange={(event) => onSwitchChange(event.target.checked)}
                            />
                        </Box>
                        {extraFilters}
                        <DatePicker
                            startDate={startDate}
                            setStartDate={onStartDateChange}
                            endDate={endDate}
                            setEndDate={onEndDateChange}
                        />
                        <Button
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                display: 'flex',
                                gap: '5px',
                                flexShrink: 0,
                            }}
                            onClick={onClearFilters}>
                            <ClearIcon />
                            <span
                                style={{
                                    marginLeft: '5px',
                                    color: '#425466',
                                    fontSize: '13px',
                                }}>
                                Clear All
                            </span>
                        </Button>
                    </Stack>
                </Box>
                {Object.keys(filters).length > 0 && (
                    <Box sx={{ marginTop: '8px' }}>
                        <Filters filters={filters} handleRemoveFilter={onRemoveFilter} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ProjectsSubHeader;